/* Canonical country list for v2 forms.
 *
 * Cleaned + deduplicated from the legacy DemoRequestForm list (which had
 * Cambodia / Ecuador / South Africa repeated). Used by RequestDemoV2 and
 * ContactUsV2.
 *
 * Localisation strategy
 * ---------------------
 * We keep ISO 3166-1 alpha-2 codes as the source of truth and use
 * `Intl.DisplayNames(locale, {type: 'region'})` to render the country
 * names in the visitor's locale. The form `value` we send to the API is
 * always the canonical English name, so downstream CRM payloads stay
 * stable across locales and we only translate the *label* the user sees.
 *
 * The legacy `COUNTRIES` export (array of English names) is preserved
 * for any caller that still depends on it.
 */

const COUNTRY_CODES = [
  'AF', 'AL', 'DZ', 'AD', 'AO',
  'AG', 'AR', 'AM', 'AU', 'AT',
  'AZ', 'BS', 'BH', 'BD', 'BB',
  'BY', 'BE', 'BZ', 'BJ', 'BT',
  'BO', 'BA', 'BW', 'BR', 'BN',
  'BG', 'BF', 'BI', 'KH', 'CV',
  'CM', 'CA', 'CF', 'TD', 'CL',
  'CN', 'CO', 'KM', 'CG', 'CD',
  'CR', 'HR', 'CU', 'CY', 'CZ',
  'DK', 'DJ', 'DM', 'DO', 'EC',
  'EG', 'SV', 'GQ', 'ER', 'EE',
  'SZ', 'ET', 'FJ', 'FI', 'FR',
  'GA', 'GM', 'GE', 'DE', 'GH',
  'GR', 'GD', 'GT', 'GN', 'GW',
  'GY', 'HT', 'HN', 'HU', 'IS',
  'IN', 'ID', 'IR', 'IQ', 'IE',
  'IL', 'IT', 'CI', 'JM', 'JP',
  'JO', 'KZ', 'KE', 'KI', 'XK',
  'KW', 'KG', 'LA', 'LV', 'LB',
  'LS', 'LR', 'LY', 'LI', 'LT',
  'LU', 'MG', 'MW', 'MY', 'MV',
  'ML', 'MT', 'MH', 'MR', 'MU',
  'MX', 'FM', 'MD', 'MC', 'MN',
  'ME', 'MA', 'MZ', 'MM', 'NA',
  'NR', 'NP', 'NL', 'NZ', 'NI',
  'NE', 'NG', 'KP', 'MK', 'NO',
  'OM', 'PK', 'PW', 'PS', 'PA',
  'PG', 'PY', 'PE', 'PH', 'PL',
  'PT', 'QA', 'RO', 'RU', 'RW',
  'KN', 'LC', 'VC', 'WS', 'SM',
  'ST', 'SA', 'SN', 'RS', 'SC',
  'SL', 'SG', 'SK', 'SI', 'SB',
  'SO', 'ZA', 'KR', 'SS', 'ES',
  'LK', 'SD', 'SR', 'SE', 'CH',
  'SY', 'TW', 'TJ', 'TZ', 'TH',
  'TL', 'TG', 'TO', 'TT', 'TN',
  'TR', 'TM', 'TV', 'UG', 'UA',
  'AE', 'GB', 'US', 'UY', 'UZ',
  'VU', 'VA', 'VE', 'VN', 'YE',
  'ZM', 'ZW'
];

/* Local overrides where Intl's region name does not match the spelling
 * we have used historically. Kosovo (XK) is a user-assigned code with
 * no canonical Intl entry; the rest pin our preferred wording. */
const ENGLISH_OVERRIDES = {
  XK: 'Kosovo',
  CG: 'Congo (Brazzaville)',
  CD: 'Congo (Kinshasa)',
  CV: 'Cabo Verde',
  CI: 'Ivory Coast',
  MM: 'Myanmar (Burma)',
  KP: 'North Korea',
  KR: 'South Korea',
  SZ: 'Eswatini',
  CZ: 'Czech Republic',
  VA: 'Vatican City',
  PS: 'Palestine'
};

const LOCALE_OVERRIDES = {
  es: {XK: 'Kosovo', PS: 'Palestina'},
  de: {XK: 'Kosovo', PS: 'Palästina'},
  fr: {XK: 'Kosovo', PS: 'Palestine'},
  it: {XK: 'Kosovo', PS: 'Palestina'}
};

function regionName(code, locale) {
  const override = LOCALE_OVERRIDES[locale]?.[code];
  if (override) return override;
  if (locale === 'en' && ENGLISH_OVERRIDES[code]) return ENGLISH_OVERRIDES[code];
  try {
    const dn = new Intl.DisplayNames([locale], {type: 'region'});
    const name = dn.of(code);
    if (name && name !== code) return name;
  } catch {
    // Intl may not have data for the runtime; fall back to the English label.
  }
  return ENGLISH_OVERRIDES[code] || code;
}

/* Returns `[{value, label}]` options sorted by the visitor's locale.
 * `value` is always the canonical English name so API payloads stay
 * stable across locales; `label` is the localised display name. */
export function getCountryOptions(locale = 'en') {
  const collator = new Intl.Collator(locale, {sensitivity: 'base'});
  const options = COUNTRY_CODES.map((code) => ({
    value: regionName(code, 'en'),
    label: regionName(code, locale)
  }));
  options.sort((a, b) => collator.compare(a.label, b.label));
  return options;
}

/* Legacy export — list of canonical English names. Kept so any caller
 * that has not switched to `getCountryOptions(locale)` still works. */
export const COUNTRIES = COUNTRY_CODES.map((code) => regionName(code, 'en'));
